import { useEffect, useMemo, useRef } from 'react';

import { useChatStore } from '@stores/chat.store';
import { useAnalytics } from '@hooks/analytics';

import { getTimeDifference, formatStatusMessage } from '@/utils/helpers';
import {
	getMessagesFromLastLead,
	getMessagesFromFirstVendorResponse,
	countMyMessages,
} from './utils/analytics-response-time.helpers';

/** Dispara evento de analytics com o tempo de primeira resposta do vendedor ou comprador. */
export const useAnalyticsResponseTime = (): void => {
	const { trackEvent } = useAnalytics();

	const messageListSource = useChatStore(s => s.messageList.source);
	const newMessage = useChatStore(s => s.newMessage);
	const tabConversation = useChatStore(s => s.tabs.conversation);

	const firedRef = useRef<string | null>(null);

	const firstMessage = messageListSource?.[0];
	const statusConversa = useMemo(
		() => formatStatusMessage(firstMessage?.statusMessage, !!firstMessage?.itsMyMessage),
		[firstMessage?.statusMessage, firstMessage?.itsMyMessage],
	);

	useEffect(() => {
		if (!newMessage.fetched || !newMessage.source?.length || !messageListSource?.length) return;

		const newMsg = newMessage.source[0];
		if (!newMsg) return;

		const chatId = newMessage.chatId;
		if (firedRef.current === chatId) return;

		const { sentOn: newMessageSentTime, itsMyMessage: isMyMessage } = newMsg;
		if (!isMyMessage || !newMessageSentTime) return;

		const messagesFromLastLead = getMessagesFromLastLead(messageListSource);
		if (!messagesFromLastLead.length) return;

		if (tabConversation === 1 && countMyMessages(messagesFromLastLead) === 1) {
			const leadSentTime = messagesFromLastLead[0]?.sentOn;
			if (!leadSentTime) return;

			firedRef.current = chatId ?? null;
			trackEvent('chatRespostaVendedor', {
				pageName: '/webmotors/garagem/novo-chat/venda',
				pageType: 'chat-venda',
				flowType: 'garagem',
				use: ['site', 'user', 'page'],
				tier: 3,
				recurringEvent: true,
				user: {
					statusConversa,
					tempoRespostaVendedor: getTimeDifference(leadSentTime, newMessageSentTime),
				},
			});
			return;
		}

		if (tabConversation === 0) {
			const messagesFromFirstVendor = getMessagesFromFirstVendorResponse(messagesFromLastLead);
			if (countMyMessages(messagesFromFirstVendor) !== 1) return;

			const firstVendorSentTime = messagesFromFirstVendor[0]?.sentOn;
			if (!firstVendorSentTime) return;

			firedRef.current = chatId ?? null;
			trackEvent('chatRespostaComprador', {
				pageName: '/webmotors/garagem/novo-chat/compra',
				pageType: 'chat-compra',
				flowType: 'garagem',
				use: ['site', 'user', 'page'],
				tier: 3,
				recurringEvent: true,
				user: {
					statusConversa,
					tempoRespostaComprador: getTimeDifference(firstVendorSentTime, newMessageSentTime),
				},
			});
		}
	}, [newMessage, messageListSource, tabConversation, statusConversa, trackEvent]);
};
